import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getClients, createClient, deleteClient, getProjects } from "../api/api";
import { LoadingState, ErrorState, EmptyState } from "../components/LoadingState";
import Pagination from "../components/Pagination";
import * as yup from "yup";
import "./Client.css";

// ── Social icons ──────────────────────────────────────────────
const SocialIcons = {
  facebook:  { icon: "f",  color: "#3b82f6", bg: "#1d3461" },
  instagram: { icon: "📷", color: "#e1306c", bg: "#3d1020" },
  whatsapp:  { icon: "💬", color: "#25d366", bg: "#0d2e1a" },
  tiktok:    { icon: "♪",  color: "#ffffff", bg: "#1a1a1a" },
  linkedin:  { icon: "in", color: "#0a66c2", bg: "#0a2540" },
  snapchat:  { icon: "👻", color: "#fffc00", bg: "#2a2800" },
};

const SOCIAL_FIELDS = ["facebook","instagram","whatsapp","tiktok","linkedin","snapchat"];

// ── File helpers ──────────────────────────────────────────────
const FILE_ICONS = {
  pdf:"📄", doc:"📝", docx:"📝", xls:"📊", xlsx:"📊",
  png:"🖼️", jpg:"🖼️", jpeg:"🖼️", gif:"🖼️", webp:"🖼️",
  zip:"🗜️", rar:"🗜️", mp4:"🎬", mov:"🎬", mp3:"🎵", wav:"🎵",
};
const getFileIcon = (name = "") => FILE_ICONS[name.split(".").pop().toLowerCase()] ?? "📎";
const formatBytes = (b) => b < 1024 ? b+" B" : b < 1048576 ? (b/1024).toFixed(1)+" KB" : (b/1048576).toFixed(1)+" MB";
const ATTACHMENT_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar,.mp4,.mov,.mp3,.wav";

function getInitials(name = "") {
  return (name || "").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

const statusLabel = { active: "Active", inactive: "Inactive", suspended: "Suspended" };
const normalizeClient = (c = {}) => {
  const phoneFromContact = c.contactInfo?.find(x => x.type === "phone")?.value || "";
  return {
    _id:         c._id || c.id,
    name:        c.name || "",
    email:       c.email || "",
    phone:       c.phone || phoneFromContact,
    company:     c.company || "",
    status:      c.status || "active",
    contactInfo: c.contactInfo || [],
  };
};

const emptyForm = () => ({
  name: "", email: "", phone: "", company: "", status: "active",
  facebook: "", instagram: "", whatsapp: "",
  tiktok: "", linkedin: "", snapchat: "",
  projects: [],       // array of project IDs
  notes: "",
  agreedPrice: "",
  earned: "",
  contract: null,
  attachments: [],    // File objects
  transactions: [],   // File objects
});

const validationSchema = yup.object().shape({
  name:  yup.string().required("Name is required").min(2, "Name must be at least 2 characters"),
  email: yup.string().required("Email is required").email("Please enter a valid email address"),
  phone: yup.string().matches(/^[\d\s+()\-]*$/, "Please enter a valid phone number"),
  company:     yup.string(),
  agreedPrice: yup.number().typeError("Must be a number").positive("Must be positive").nullable(),
  earned:      yup.number().typeError("Must be a number").min(0, "Must be 0 or greater").nullable(),
});

export default function Clients() {
  const navigate = useNavigate();
  const transactionRef = useRef();
  const attachRef      = useRef();

  const [clients,      setClients]      = useState([]);
  const [projects,     setProjects]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal,    setShowModal]    = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [form,         setForm]         = useState(emptyForm());
  const [formErrors,   setFormErrors]   = useState({});
  const [currentPage,  setCurrentPage]  = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { fetchClients(); fetchProjects(); }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const res  = await getClients();
      const data = res?.data?.data ?? res?.data ?? res ?? [];
      setClients(Array.isArray(data) ? data.map(normalizeClient) : []);
    } catch {
      setError("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res  = await getProjects();
      const data = res?.data?.data ?? res?.data ?? res ?? [];
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    }
  };

  const filtered = clients.filter(c => {
    const matchesSearch = [c.name, c.email, c.company, c.phone].some(f =>
      (f || "").toLowerCase().includes(search.toLowerCase())
    );
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedClients = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [search, statusFilter]);

  // ── Project toggle in modal ───────────────────────────────────
  const toggleModalProject = (projectId) => {
    setForm(prev => {
      const exists  = prev.projects.includes(projectId);
      const updated = exists
        ? prev.projects.filter(id => id !== projectId)
        : [...prev.projects, projectId];
      return { ...prev, projects: updated };
    });
  };

  // ── Transaction file pick ─────────────────────────────────────
  const handleTransactionPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setForm(prev => ({ ...prev, transactions: [...prev.transactions, ...files] }));
    e.target.value = "";
  };

  const removeTransaction = (index) => {
    setForm(prev => ({ ...prev, transactions: prev.transactions.filter((_, i) => i !== index) }));
  };

  // ── Attachment file pick ──────────────────────────────────────
  const handleAttachmentPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setForm(prev => ({ ...prev, attachments: [...prev.attachments, ...files] }));
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setForm(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  // ── Add client ────────────────────────────────────────────────
  const handleAdd = async () => {
    try {
      await validationSchema.validate(form, { abortEarly: false });
      setFormErrors({});
    } catch (err) {
      const errors = {};
      err.inner.forEach(e => { errors[e.path] = e.message; });
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name:     form.name,
        email:    form.email,
        phone:    form.phone,
        company:  form.company,
        status:   form.status,
        contactInfo: SOCIAL_FIELDS
          .filter(k => form[k])
          .map(k => ({ type: k, value: form[k] })),
        projects:  form.projects,
        notes:     form.notes || "",
        agreedPrice: form.agreedPrice || "",
        earned:    form.earned || "",
        contract:  form.contract,
        // transaction files & attachment files passed separately
      };
      const res     = await createClient(
        payload,
        form.contract,
        form.attachments.length  > 0 ? form.attachments  : null,
        form.transactions.length > 0 ? form.transactions : null,
      );
      const created = normalizeClient(res?.data || res);
      setClients(prev => [...prev, created]);
      setShowModal(false);
      setForm(emptyForm());
      setFormErrors({});
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create client");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    const confirmed = window.confirm("Are you sure you want to delete this client?");
    if (!confirmed) return;
    try {
      await deleteClient(id);
      setClients(prev => prev.filter(c => c._id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete client");
    }
  };

  /* ── Render ── */
  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
      <div style={{ color:"#4b5563", fontSize:15 }}>Loading…</div>
    </div>
  );

  if (error) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:12 }}>
      <div style={{ color:"#f87171", fontSize:15 }}>{error}</div>
      <button onClick={fetchClients} style={{ padding:"10px 24px", background:"#facc15", color:"#0a0f1a", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer" }}>
        Retry
      </button>
    </div>
  );

  return (
    <div className="clients-page">

      {/* Header */}
      <div className="clients-header">
        <div>
          <h1>Clients</h1>
          <p>{clients.length} clients total</p>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button className="add-client-btn" onClick={() => navigate("/clients/deleted")} style={{ background:"#1a2540", color:"#94a3b8" }}>
            🗑️ Deleted Clients
          </button>
          <button className="add-client-btn" onClick={() => setShowModal(true)}>
            + Add Client
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="search-bar">
        <span className="search-icon">🔍</span>
        <input
          placeholder="Search by name, email or company…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="status-filter-dropdown"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={setCurrentPage}
      />

      {/* Table */}
      <div className="clients-table-wrapper">
        <table className="clients-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Social</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6}><div className="empty-state"><p>No clients found.</p></div></td></tr>
            ) : (
              paginatedClients.map(client => (
                <tr key={client._id} onClick={() => navigate(`/clients/${client._id}`, { state: { client } })}>
                  <td>
                    <div className="client-name-cell">
                      <div className="client-avatar">{getInitials(client.name)}</div>
                      <div>
                        <div className="client-name-text">{client.name}</div>
                        <div className="client-company">{client.company}</div>
                      </div>
                    </div>
                  </td>
                  <td>{client.email}</td>
                  <td>{client.phone}</td>
                  <td>
                    <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                      {(client.contactInfo || []).filter(c => c.value).map(c => {
                        const s = SocialIcons[c.type];
                        if (!s) return null;
                        return (
                          <a
                            key={c.type}
                            href={c.type === "whatsapp" ? `https://wa.me/${c.value}` : c.value}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            title={c.type}
                            style={{
                              width:26, height:26, borderRadius:6,
                              background:s.bg, color:s.color,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:11, fontWeight:800,
                              border:`1px solid ${s.color}30`,
                              textDecoration:"none", flexShrink:0,
                            }}
                          >
                            {s.icon}
                          </a>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge status-${client.status}`}>
                      {statusLabel[client.status] || client.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions" onClick={e => e.stopPropagation()}>
                      <button className="action-btn" onClick={() => navigate(`/clients/${client._id}`, { state: { client } })}>
                        View
                      </button>
                      <button className="action-btn" onClick={e => handleDelete(e, client._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Add Client Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add New Client</div>

            {/* Basic info */}
            <div className="modal-section-label">Basic Information</div>
            <div className="modal-grid">
              {[
                { key:"name",    label:"Name *",  ph:"Full name" },
                { key:"email",   label:"Email *", ph:"name@company.com" },
                { key:"phone",   label:"Phone",   ph:"+20 100 000 0000" },
                { key:"company", label:"Company", ph:"Company name" },
              ].map(({ key, label, ph }) => (
                <div className="modal-field" key={key}>
                  <label>{label}</label>
                  <input
                    value={form[key]}
                    placeholder={ph}
                    onChange={e => setForm({ ...form, [key]: e.target.value })}
                    className={formErrors[key] ? "input-error" : ""}
                  />
                  {formErrors[key] && <span className="error-message">{formErrors[key]}</span>}
                </div>
              ))}
              <div className="modal-field">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>

            {/* Social media */}
            <div className="modal-section-label">Social Media</div>
            <div className="modal-grid">
              {SOCIAL_FIELDS.map(key => {
                const s = SocialIcons[key];
                return (
                  <div className="modal-field" key={key}>
                    <label style={{ textTransform:"capitalize" }}>{key}</label>
                    <div className="social-input-wrap">
                      <span className="s-icon" style={{ color:s.color }}>{s.icon}</span>
                      <input
                        value={form[key]}
                        placeholder={key === "whatsapp" ? "+201234567890" : `https://${key}.com/...`}
                        onChange={e => setForm({ ...form, [key]: e.target.value })}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Projects — pill toggle */}
            <div className="modal-section-label">Projects</div>
            {projects.length === 0 ? (
              <div style={{ color:"#4b5563", fontSize:13, marginBottom:12 }}>No projects available.</div>
            ) : (
              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:16 }}>
                {projects.map(project => {
                  const selected = form.projects.includes(project._id);
                  return (
                    <button
                      key={project._id}
                      type="button"
                      onClick={() => toggleModalProject(project._id)}
                      style={{
                        padding:"7px 14px",
                        borderRadius:8,
                        border: selected ? "1px solid #facc15" : "1px solid #1e2a45",
                        background: selected ? "#facc1520" : "#0d1526",
                        color: selected ? "#facc15" : "#6b7280",
                        fontSize:13,
                        fontWeight: selected ? 600 : 400,
                        cursor:"pointer",
                        transition:"all 0.15s",
                        display:"flex",
                        alignItems:"center",
                        gap:6,
                      }}
                    >
                      {selected && <span>✓</span>}
                      {project.title || project.name}
                    </button>
                  );
                })}
              </div>
            )}
            {form.projects.length > 0 && (
              <div style={{ marginBottom:12, fontSize:12, color:"#4b5563" }}>
                {form.projects.length} project{form.projects.length > 1 ? "s" : ""} selected
              </div>
            )}

            {/* Notes */}
            <div className="modal-section-label">Notes</div>
            <div className="modal-field">
              <label>Notes about client</label>
              <textarea
                rows={3}
                value={form.notes}
                placeholder="Any notes about this client"
                onChange={e => setForm({ ...form, notes: e.target.value })}
                style={{ width:"100%", padding:8, resize:"vertical" }}
              />
            </div>

            {/* Pricing */}
            <div className="modal-section-label">Pricing</div>
            <div className="modal-grid">
              <div className="modal-field">
                <label>Agreed Price (EGP)</label>
                <input
                  type="number"
                  value={form.agreedPrice}
                  placeholder="0"
                  onChange={e => setForm({ ...form, agreedPrice: e.target.value })}
                  className={formErrors.agreedPrice ? "input-error" : ""}
                />
                {formErrors.agreedPrice && <span className="error-message">{formErrors.agreedPrice}</span>}
              </div>
              <div className="modal-field">
                <label>Earned (EGP)</label>
                <input
                  type="number"
                  value={form.earned}
                  placeholder="0"
                  onChange={e => setForm({ ...form, earned: e.target.value })}
                  className={formErrors.earned ? "input-error" : ""}
                />
                {formErrors.earned && <span className="error-message">{formErrors.earned}</span>}
              </div>
            </div>

            {/* Contract */}
            <div className="modal-section-label">Contract</div>
            <div className="modal-field">
              {form.contract ? (
                <div className="file-item" style={{ border:"1px solid #facc1540", borderRadius:8, padding:"10px 12px" }}>
                  <div className="file-info">
                    <span className="file-icon">{getFileIcon(form.contract.name)}</span>
                    <div>
                      <div className="file-name">{form.contract.name}</div>
                      <div style={{ fontSize:11, color:"#facc15" }}>{formatBytes(form.contract.size)}</div>
                    </div>
                  </div>
                  <button className="file-action-btn" onClick={() => setForm(prev => ({ ...prev, contract: null }))}>
                    Remove
                  </button>
                </div>
              ) : (
                <label style={{
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                  gap:6, padding:"20px 12px", border:"1px dashed #1e2a45", borderRadius:8,
                  cursor:"pointer", color:"#4b5563", fontSize:13,
                }}>
                  <span style={{ fontSize:24 }}>📄</span>
                  <span>Click to upload contract</span>
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={e => setForm(prev => ({ ...prev, contract: e.target.files[0] || null }))}
                  />
                </label>
              )}
            </div>

            {/* Attachments */}
            <div className="modal-section-label">Attachments</div>
            <div className="modal-field">
              {form.attachments.map((file, i) => (
                <div key={i} className="file-item" style={{ marginBottom:6, border:"1px solid #facc1530", borderRadius:8, padding:"8px 12px" }}>
                  <div className="file-info">
                    <span className="file-icon">{getFileIcon(file.name)}</span>
                    <div>
                      <div className="file-name">{file.name}</div>
                      <div style={{ fontSize:11, color:"#4b5563" }}>{formatBytes(file.size)}</div>
                    </div>
                  </div>
                  <button className="file-action-btn" onClick={() => removeAttachment(i)}>Remove</button>
                </div>
              ))}
              <button
                type="button"
                className="upload-btn"
                onClick={() => attachRef.current.click()}
                style={{ width:"100%", marginTop: form.attachments.length > 0 ? 8 : 0 }}
              >
                + Add Attachment
              </button>
              <input ref={attachRef} type="file" hidden multiple accept={ATTACHMENT_ACCEPT} onChange={handleAttachmentPick} />
            </div>

            {/* Transactions — file upload */}
            <div className="modal-section-label">Transactions</div>
            <div className="modal-field">
              {form.transactions.map((file, i) => (
                <div key={i} className="file-item" style={{ marginBottom:6, border:"1px solid #facc1530", borderRadius:8, padding:"8px 12px" }}>
                  <div className="file-info">
                    <span className="file-icon">{getFileIcon(file.name)}</span>
                    <div>
                      <div className="file-name">{file.name}</div>
                      <div style={{ fontSize:11, color:"#4b5563" }}>{formatBytes(file.size)}</div>
                    </div>
                  </div>
                  <button className="file-action-btn" onClick={() => removeTransaction(i)}>Remove</button>
                </div>
              ))}
              <button
                type="button"
                className="upload-btn"
                onClick={() => transactionRef.current.click()}
                style={{ width:"100%", marginTop: form.transactions.length > 0 ? 8 : 0 }}
              >
                + Add Transaction File
              </button>
              <input ref={transactionRef} type="file" hidden multiple accept={ATTACHMENT_ACCEPT} onChange={handleTransactionPick} />
              <div style={{ fontSize:11, color:"#4b5563", marginTop:6 }}>
                Upload receipts, invoices, or any transaction documents.
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-primary" onClick={handleAdd} disabled={submitting}>
                {submitting ? "Creating…" : "Create Client"}
              </button>
              <button className="btn-cancel" onClick={() => { setShowModal(false); setFormErrors({}); setForm(emptyForm()); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}